import './RegisterForm.scss';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  IonText,
  IonItem,
  IonLabel,
  IonButton,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';

import { rolesData, statesData } from 'global';
import ClinicModal from 'components/ModalClinics/ModalClinics';
import ControlledCheckbox from 'components/ControlledCheckbox/ControlledCheckbox';

// Sentinel used both as the state_selection value and as the clinic crm_id
// when the registrant is International / Puerto Rico. Keeps that population
// distinguishable in the DB without leaking into the 50-state dropdowns
// elsewhere (shipping, etc.).
const INTL_PR = 'INTL-PR';

interface RegisterFormProps {
  onSubmit: (data: object) => {};
}

interface ErrorMessageProps {
  text?: string;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
  text = 'This field is required.',
}) => (
  <p className="text-small mt-2">
    <IonText color="danger">{text}</IonText>
  </p>
);

const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit }) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm({ mode: 'onChange' });
  const checkBoxChecked = watch('over18', false);
  const stateSelection = watch('state_selection');
  const watchClinic = watch('clinic');

  const isInternational = stateSelection === INTL_PR;
  const isUsState = Boolean(stateSelection) && !isInternational;

  const addOwnClinic = watchClinic?.clinic_name === 'Add My Own Clinic';
  const addStudentOrVet =
    watchClinic?.clinic_name === 'Student' ||
    watchClinic?.clinic_name === 'Not Applicable' ||
    watchClinic?.clinic_name === 'Relief Vet';

  // React to state changes: auto-fill INTL-PR clinic (skipping the modal),
  // reset clinic on US-state changes so a stale TX pick doesn't ride along
  // when the user switches to CA, and mirror the picked US state into the
  // address-block `state` field so Add-My-Own-Clinic isn't asked twice.
  useEffect(() => {
    if (isInternational) {
      setValue(
        'clinic',
        { clinic_name: 'International / Puerto Rico', crm_id: INTL_PR },
        { shouldValidate: true },
      );
    } else if (isUsState) {
      setValue('clinic', { clinic_name: '', crm_id: '' }, { shouldValidate: true });
      setValue('state', stateSelection, { shouldValidate: true });
    } else {
      setValue('clinic', { clinic_name: '', crm_id: '' }, { shouldValidate: true });
    }
  }, [stateSelection, isInternational, isUsState, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="elanco--form">
        <div className="elanco--item">
          <label id="first_name">
            First Name <IonText color="danger">*</IonText>
          </label>
          <input type="text" {...register('first_name', { required: true })} />
          {errors.first_name && <ErrorMessage />}
        </div>
        <div className="elanco--item">
          <label id="last_name">
            Last Name <IonText color="danger">*</IonText>
          </label>
          <input type="text" {...register('last_name', { required: true })} />
          {errors.last_name && <ErrorMessage />}
        </div>
        <div className="elanco--item">
          <label id="email">
            Email <IonText color="danger">*</IonText>
          </label>
          <input
            type="email"
            {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
          />
          {errors?.email?.type === 'required' && <ErrorMessage />}
          {errors?.email?.type === 'pattern' && (
            <ErrorMessage text="Email format is incorrect!" />
          )}
        </div>

        <div className="elanco--item">
          <label>
            Role <IonText color="danger">*</IonText>
          </label>

          <IonSelect
            interface="popover"
            placeholder=""
            {...register('your_role', { required: true })}>
            {rolesData.map((option: any, i: number) => (
              <IonSelectOption key={i} value={option.value}>
                {option.name}
              </IonSelectOption>
            ))}
          </IonSelect>

          {errors.your_role && <ErrorMessage />}
        </div>

        <div className="elanco--item">
          <label>
            State <IonText color="danger">*</IonText>
          </label>

          <Controller
            name="state_selection"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <IonSelect
                interface="popover"
                placeholder=""
                value={value ?? ''}
                onIonChange={(e) => onChange(e.detail.value)}
                onIonBlur={onBlur}>
                <IonSelectOption value={INTL_PR}>
                  International / Puerto Rico
                </IonSelectOption>
                {statesData.map((option: any, i: number) => (
                  <IonSelectOption key={i} value={option.value}>
                    {option.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            )}
          />

          {errors.state_selection && <ErrorMessage />}
        </div>

        <Controller
          name="clinic"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, onBlur, value } }) => (
            <ClinicModal
              {...{ onChange, onBlur, value }}
              state={isUsState ? String(stateSelection) : ''}
              disabled={!isUsState}
            />
          )}
        />

        {addOwnClinic && (
          <div className="elanco--item">
            <label id="ownClinic">
              Enter Your Clinic Name <IonText color="danger">*</IonText>
            </label>
            <input
              type="text"
              {...register('ownClinic', { required: addOwnClinic })}
            />
            {errors.ownClinic && <ErrorMessage />}
          </div>
        )}

        {(addStudentOrVet || addOwnClinic) && (
          <>
            <div className="elanco--item">
              <label id="address-line-1">
                Address <IonText color="danger">*</IonText>
              </label>
              <input
                // name="zip"
                type="text"
                {...register('addressLine', {
                  minLength: 5,
                  required: addOwnClinic,
                })}
              />

              {errors?.addressLine?.type === 'required' && <ErrorMessage />}
              {errors?.addressLine?.type === 'minLength' && (
                <ErrorMessage text="Min length is 5!" />
              )}
            </div>

            <div className="elanco--item">
              <label id="city">
                City <IonText color="danger">*</IonText>
              </label>
              <input
                // name="zip"
                type="text"
                {...register('city', { required: addOwnClinic })}
              />
              {errors.city && <ErrorMessage />}
            </div>

            <div className="elanco--item">
              <label>
                State <IonText color="danger">*</IonText>
              </label>

              <IonSelect
                interface="popover"
                placeholder=""
                {...register('state', { required: true })}>
                {statesData.map((option: any, i: number) => (
                  <IonSelectOption key={i} value={option.value}>
                    {option.name}
                  </IonSelectOption>
                ))}
              </IonSelect>

              {errors.state && <ErrorMessage />}
            </div>

            <div className="elanco--item">
              <label id="zip">
                Clinic Zip <IonText color="danger">*</IonText>
              </label>
              <input
                maxLength={5}
                type="text"
                {...register('zip', { maxLength: 5, required: addOwnClinic })}
              />
              {errors.zip && <ErrorMessage />}
            </div>
          </>
        )}
      </div>

      <IonText color="white" className="text-small">
        Your privacy is important to us. To find out more about the information
        that Elanco may collect, how we use it, how we protect it, and your
        rights and choices with respect to your personal data,{' '}
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.elancostatements.com/en/privacy/elanco-com">
          click here
        </a>
        .
      </IonText>

      <IonItem className="text-small">
        <IonLabel color="white">
          I certify that I am at least 18 years old.
          <IonText color="danger">*</IonText>
        </IonLabel>
        <ControlledCheckbox name="over18" control={control} required={true} />
      </IonItem>

      <div className="ion-text-center pt-5 pb-10">
        <IonButton
          type="submit"
          strong
          {...(!isValid || isSubmitting || checkBoxChecked !== true
            ? { disabled: true }
            : {})}>
          Submit
        </IonButton>
      </div>
    </form>
  );
};

export default RegisterForm;
